/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Modal, Form, Input, message, Alert } from 'antd';
import { useRef, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

export interface CreateFolderModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (folderData: { name: string; description: string }) => void;
  parentFolderName?: string;
  existingFolderNames?: string[];
  parentFolderPath?: string[];
}

export const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  visible,
  onCancel,
  onCreate,
  parentFolderName,
  existingFolderNames,
  parentFolderPath,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [currentFolderName, setCurrentFolderName] = useState('');
  const nameInputRef = useRef<Input>(null);

  useEffect(() => {
    if (visible) {
      setTimeout(() => nameInputRef.current?.focus(), 350);
    }
  }, [visible]);

  const checkForDuplicate = (value: string) => {
    if (
      value &&
      existingFolderNames?.some(
        (name) => name.trim().toLowerCase() === value.trim().toLowerCase()
      )
    ) {
      setShowDuplicateWarning(true);
    } else {
      setShowDuplicateWarning(false);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onCreate({
        name: values.name,
        description: values.description || '',
      });
      form.resetFields();
      setShowDuplicateWarning(false);
      setCurrentFolderName('');
      onCancel();
    } catch (error) {
      // Validation failed
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setShowDuplicateWarning(false);
    setCurrentFolderName('');
    onCancel();
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={
            parentFolderName
              ? `${intl.formatMessage({
                  id: 'create-new-folder',
                })} in "${parentFolderName}"`
              : intl.formatMessage({ id: 'create-new-folder' })
          }
          icon={['far', 'folder-plus']}
        />
      }
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={intl.formatMessage({ id: 'create' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      confirmLoading={loading}
      centered>
      {parentFolderPath && parentFolderPath.length > 0 && (
        <div css={styles.pathDisplay}>
          <span css={styles.pathLabel}>
            {intl.formatMessage({ id: 'location' })}:
          </span>
          <span css={styles.pathContent}>
            root / {parentFolderPath.join(' / ')} /
            {currentFolderName && (
              <span css={styles.currentFolderName}> {currentFolderName}</span>
            )}
          </span>
        </div>
      )}
      <Form form={form} layout="vertical" name="createFolderForm">
        <Form.Item
          name="name"
          label={intl.formatMessage({ id: 'folder-name' })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: 'folder-name-required' }),
            },
          ]}>
          <Input
            ref={nameInputRef}
            placeholder={intl.formatMessage({ id: 'enter-folder-name' })}
            onPressEnter={handleOk}
            onChange={(e) => {
              checkForDuplicate(e.target.value);
              setCurrentFolderName(e.target.value);
            }}
          />
        </Form.Item>
        {showDuplicateWarning && (
          <Alert
            message={intl.formatMessage({ id: 'folder-name-already-exists' })}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="description"
          label={intl.formatMessage({ id: 'description' })}>
          <Input.TextArea
            rows={3}
            placeholder={intl.formatMessage({
              id: 'enter-folder-description',
            })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  pathDisplay: {
    marginBottom: 16,
    padding: '8px 12px',
    backgroundColor: theme.colors.backgroundSecondary,
    borderRadius: 4,
    fontSize: 12,
  },
  pathLabel: {
    fontWeight: 600,
    color: theme.colors.textSecondary,
    marginRight: 8,
  },
  pathContent: {
    color: theme.colors.textPrimary,
    fontFamily: 'monospace',
  },
  currentFolderName: {
    color: theme.colors.primary,
    fontWeight: 600,
  },
});
